/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { navigableBreadcrumbLinkSx } from "./style";

/**
 * A reusable breadcrumb link with router navigation and optional route state.
 *
 * Contract for LinkComponent:
 * - Must accept: color, component, sx, onClick, children.
 * - Intended usage is a styled typography/link wrapper (e.g. StyledLink) that forwards these props.
 *
 * @param {Object} props
 * @param {React.ElementType} props.LinkComponent Component used to render the clickable breadcrumb text.
 * @param {string} props.label Display label for the breadcrumb item.
 * @param {string} props.to Destination route path.
 * @param {string} [props.color="#2583e3"] Text color forwarded to LinkComponent.
 * @param {Object} [props.state] Optional location state forwarded to react-router navigate.
 * @param {Object} [props.sx=navigableBreadcrumbLinkSx] Style overrides forwarded to LinkComponent.
 * @returns {JSX.Element}
 */

/**
 * NavigableBreadcrumbLink
 *
 * Props: to, state, label, LinkComponent
 *
 * Note: `replace` is not currently supported. If you need replace-history
 * behaviour in a breadcrumb, raise a follow-up ticket and extend this component.
 */

export const NavigableBreadcrumbLink = ({
  LinkComponent,
  label,
  to,
  color = "#2583e3",
  state,
  sx = navigableBreadcrumbLinkSx,
  dataTestId,
}) => {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (state === undefined) {
      navigate(to);
      return;
    }

    navigate(to, { state });
  }, [navigate, state, to]);

  return (
    <LinkComponent data-testid={dataTestId} color={color} component="span" sx={sx} onClick={handleClick}>
      {label}
    </LinkComponent>
  );
};
